import React from "react";
import ReactTooltip from "react-tooltip";

import InputFormField from "../../components/inputform/InputFormField";
import { Side, Size } from "../../types/system/Layout";

import RenderOrientations, {
    RenderOrientation,
} from "../../types/enums/RenderOrientation";

import "./InputForm.scss";
import InputFormSubform from "./InputFormSubform";
import Utilities from "../../foundation/Utility";

type PartialConstructor<T> = new (data: Partial<T>) => T;

interface InputFormProps<T> {
    display?: RenderOrientation;
    fieldDisplay?: RenderOrientation;

    type?: PartialConstructor<T>;
    binding?: T | null;

    columns?: number;
    fullWidth?: boolean;
    fullHeight?: boolean;

    clearanceSide?: Side;
    clearanceSize?: Size;

    bufferSide?: Side;
    bufferSize?: Size;

    disabled?: boolean;
    className?: string;
    children?:
        | React.ReactElement<InputFormField>[]
        | React.ReactElement<InputFormField>
        | React.ReactElement<InputFormSubform>;

    onChange?: (newValue: T | null) => void;
}

interface InputFormState<T> {
    binding: T | null;
}

export const InputFormContext = React.createContext({
    binding: null as any,
    onChange: () => {},
});

class InputForm<T extends object> extends React.Component<
    InputFormProps<T>,
    InputFormState<T>
> {
    state: InputFormState<T> = {
        binding: null,
    };

    componentDidMount(): void {
        if (!!this.props.binding)
            this.setState({ binding: this.processType() });

        ReactTooltip.rebuild();
    }

    componentDidUpdate(
        prevProps: Readonly<InputFormProps<T>>,
        prevState: Readonly<InputFormState<T>>,
        snapshot?: any,
    ): void {
        if (prevProps.binding !== this.props.binding) {
            this.setState({ binding: this.processType() });
        }
    }

    processType() {
        if (!!this.props.type && !!this.props.binding) {
            return new this.props.type(structuredClone(this.props.binding));
        } else {
            return structuredClone(this.props.binding);
        }
    }

    valueChanged() {
        this.props.onChange && this.props.onChange(this.state.binding);
    }

    render(): React.ReactNode {
        let classNames = "input-form-container";

        if (this.props.className) classNames += ` ${this.props.className}`;

        // Clearance classes
        if (!!this.props.clearanceSide) {
            classNames += ` ${Utilities.getSideClass(
                this.props.clearanceSide,
            )}clearance${Utilities.getSizeClass(this.props.clearanceSize)}`;
        }

        // Buffer classes
        if (!!this.props.bufferSide) {
            classNames += ` ${Utilities.getSideClass(
                this.props.bufferSide,
            )}buffer${Utilities.getSizeClass(this.props.bufferSize)}`;
        }

        if (this.props.fullWidth) classNames += " full";
        if (this.props.fullHeight) classNames += " full-height";

        switch (this.props.display) {
            case RenderOrientations.Vertical:
                classNames += " main-vertical";
                break;
            default:
                classNames += " main-horizontal";
                if (this.props.columns)
                    classNames += ` grid-columns columns-${this.props.columns}`;
                break;
        }

        switch (this.props.fieldDisplay) {
            case RenderOrientations.Horizontal:
                classNames += " field-horizontal";
                break;
            default:
                classNames += " field-vertical";
                break;
        }

        const contextValue = {
            binding: this.state.binding,
            onChange: () => this.props.onChange?.(this.state.binding),
        };

        return (
            <InputFormContext.Provider value={contextValue}>
                <div className={classNames}>{this.props.children}</div>
            </InputFormContext.Provider>
        );
    }
}

export default InputForm;
